// EncuestaPublicPage — la encuesta que contesta el cliente, en /encuesta/:token.
// SIN sesión: el token de su enlace es la llave (Lissette 2026-10-15).
//
// Enseña solo el nombre de pila y el logo de la agencia: el enlace se puede
// reenviar y no debe dejar ver nada más del cliente. Las preguntas salen en su
// idioma (el de su ficha) y se puede cambiar con el botón ES/EN.

import React, { useEffect, useState } from "react";
import { Loader2, AlertCircle, CheckCircle, Star } from "lucide-react";
import { getEncuestaPublica, responderEncuestaPublica } from "../api.js";
import { enIdioma } from "../encuestasPlantillas.js";

const BRAND = "#0ea5e9";

function Centered({ children }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "1.5rem", background: "#f4f7fb", fontFamily: "system-ui,-apple-system,Segoe UI,sans-serif",
    }}>{children}</div>
  );
}

const card = { background: "#fff", border: "1px solid #e1e8f0", borderRadius: 14, padding: "1.1rem 1.2rem", marginBottom: ".9rem", boxShadow: "0 1px 3px rgba(0,0,0,.04)" };
const chip = (on) => ({
  padding: ".55rem .9rem", borderRadius: 10, border: `1.5px solid ${on ? BRAND : "#d5dee8"}`,
  background: on ? `${BRAND}18` : "#fff", color: "#0a1628", cursor: "pointer", fontSize: ".92rem", fontWeight: on ? 700 : 500,
});

export function EncuestaPublicPage({ token }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [lang, setLang] = useState("es");
  const [resp, setResp] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [hecha, setHecha] = useState(false);
  const [falta, setFalta] = useState(null);
  // El error AL ENVIAR va aparte: con `error` la página entera diría
  // "enlace no válido" y el cliente perdería lo que contestó.
  const [errorEnvio, setErrorEnvio] = useState("");
  const t = (es, en) => (lang === "es" ? es : en);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const d = await getEncuestaPublica(token);
        if (!vivo) return;
        setData(d);
        const pedido = new URLSearchParams(window.location.search).get("lang");
        setLang(pedido === "en" || pedido === "es" ? pedido : d?.idioma || "es");
      } catch (e) {
        if (vivo) setError(e.message || "error");
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [token]);

  if (cargando) return <Centered><Loader2 size={34} className="dkt-spin" color={BRAND} /></Centered>;

  if (error || !data) {
    return (
      <Centered>
        <AlertCircle size={40} color="#ef4444" />
        <h2 style={{ margin: "1rem 0 .4rem", color: "#0a1628" }}>{t("Enlace no válido", "Invalid link")}</h2>
        <p style={{ color: "#5b7a9a", maxWidth: 360, textAlign: "center" }}>
          {t("Esta encuesta no existe o ya no está disponible.", "This survey doesn't exist or is no longer available.")}
        </p>
      </Centered>
    );
  }

  const c = data.contenido;
  const cabecera = (
    <header style={{ background: "#fff", borderBottom: "1px solid #e1e8f0", padding: ".9rem 1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      {data.agencia?.logoUrl
        ? <img src={data.agencia.logoUrl} alt="" style={{ maxHeight: 38, maxWidth: 160, objectFit: "contain" }} />
        : <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "#0a1628" }}>{data.agencia?.nombre || ""}</div>}
      <button type="button" onClick={() => setLang(lang === "es" ? "en" : "es")}
        style={{ border: "1px solid #d5dee8", background: "#fff", borderRadius: 8, padding: ".3rem .7rem", cursor: "pointer", fontWeight: 700, color: "#0a1628" }}>
        {lang === "es" ? "EN" : "ES"}
      </button>
    </header>
  );
  const pagina = (contenido) => (
    <div style={{ minHeight: "100vh", background: `linear-gradient(180deg, ${BRAND}14 0%, #f4f7fb 260px)`, fontFamily: "system-ui,-apple-system,Segoe UI,sans-serif", color: "#0a1628" }}>
      {cabecera}
      <main style={{ maxWidth: 620, margin: "0 auto", padding: "1.4rem 1rem 3rem" }}>{contenido}</main>
    </div>
  );

  if (data.cerrada) {
    return pagina(
      <div style={{ ...card, textAlign: "center" }}>
        <h2 style={{ margin: ".2rem 0 .4rem" }}>{t("Esta encuesta ya está cerrada", "This survey is closed")}</h2>
        <p style={{ color: "#5b7a9a", margin: 0 }}>{t("Gracias de todas formas.", "Thank you anyway.")}</p>
      </div>,
    );
  }

  if (hecha || data.respondida) {
    return pagina(
      <div style={{ ...card, textAlign: "center", padding: "2rem 1.2rem" }}>
        <CheckCircle size={46} color="#10b981" />
        <h2 style={{ margin: ".8rem 0 .4rem" }}>{hecha ? t("¡Respuesta enviada!", "Response sent!") : t("Ya respondiste esta encuesta", "You already answered this survey")}</h2>
        <p style={{ color: "#5b7a9a", margin: 0, whiteSpace: "pre-wrap" }}>{enIdioma(c.gracias, lang) || t("¡Gracias!", "Thank you!")}</p>
      </div>,
    );
  }

  const poner = (id, v) => { setResp((p) => ({ ...p, [id]: v })); if (falta === id) setFalta(null); };
  const enviar = async () => {
    const sinContestar = c.preguntas.find((p) => {
      if (!p.obligatoria) return false;
      const v = resp[p.id];
      return v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length) || (typeof v === "string" && !v.trim());
    });
    if (sinContestar) {
      setFalta(sinContestar.id);
      document.getElementById(`preg-${sinContestar.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setEnviando(true);
    setErrorEnvio("");
    try {
      await responderEncuestaPublica(token, resp, lang);
      setHecha(true);
      window.scrollTo(0, 0);
    } catch (e) {
      setErrorEnvio(e.message || "error");
    } finally {
      setEnviando(false);
    }
  };

  return pagina(
    <>
      <div style={{ ...card, borderTop: `4px solid ${BRAND}` }}>
        <h1 style={{ margin: "0 0 .35rem", fontSize: "1.4rem" }}>{enIdioma(c.titulo, lang)}</h1>
        {data.nombre && <div style={{ fontWeight: 600, marginBottom: ".3rem" }}>{t(`Hola, ${data.nombre}`, `Hi, ${data.nombre}`)}</div>}
        {enIdioma(c.intro, lang) && <p style={{ margin: 0, color: "#3d5a78", whiteSpace: "pre-wrap" }}>{enIdioma(c.intro, lang)}</p>}
      </div>

      {c.preguntas.map((p, n) => {
        const v = resp[p.id];
        const opciones = p.opciones || [];
        return (
          <div key={p.id} id={`preg-${p.id}`} style={{ ...card, borderColor: falta === p.id ? "#ef4444" : "#e1e8f0" }}>
            <div style={{ fontWeight: 700, marginBottom: ".7rem" }}>
              {n + 1}. {enIdioma(p.texto, lang)} {p.obligatoria && <span style={{ color: "#ef4444" }}>*</span>}
            </div>
            {p.tipo === "estrellas" && (
              <div style={{ display: "flex", gap: 6 }} role="radiogroup">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} type="button" aria-label={`${s}`} onClick={() => poner(p.id, s)}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
                    <Star size={34} color="#f59e0b" fill={v >= s ? "#f59e0b" : "none"} />
                  </button>
                ))}
              </div>
            )}
            {p.tipo === "escala10" && (
              <>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {Array.from({ length: 11 }, (_, i) => i).map((i) => (
                    <button key={i} type="button" onClick={() => poner(p.id, i)}
                      style={{ ...chip(v === i), minWidth: 40, padding: ".5rem .3rem", textAlign: "center" }}>{i}</button>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".75rem", color: "#5b7a9a", marginTop: 6 }}>
                  <span>{t("Nada probable", "Not likely")}</span><span>{t("Muy probable", "Very likely")}</span>
                </div>
              </>
            )}
            {p.tipo === "sino" && (
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" style={chip(v === "si")} onClick={() => poner(p.id, "si")}>{t("Sí", "Yes")}</button>
                <button type="button" style={chip(v === "no")} onClick={() => poner(p.id, "no")}>No</button>
              </div>
            )}
            {p.tipo === "unica" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {opciones.map((o, i) => (
                  <button key={i} type="button" style={{ ...chip(v === i), textAlign: "left" }} onClick={() => poner(p.id, i)}>
                    {v === i ? "◉ " : "○ "}{enIdioma(o, lang)}
                  </button>
                ))}
              </div>
            )}
            {p.tipo === "varias" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {opciones.map((o, i) => {
                  const lista = Array.isArray(v) ? v : [];
                  const on = lista.includes(i);
                  return (
                    <button key={i} type="button" style={{ ...chip(on), textAlign: "left" }}
                      onClick={() => poner(p.id, on ? lista.filter((x) => x !== i) : [...lista, i])}>
                      {on ? "☑ " : "☐ "}{enIdioma(o, lang)}
                    </button>
                  );
                })}
              </div>
            )}
            {p.tipo === "texto" && (
              <textarea value={v || ""} maxLength={2000} rows={4} onChange={(e) => poner(p.id, e.target.value)}
                placeholder={t("Escribe tu respuesta…", "Type your answer…")}
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d5dee8", borderRadius: 10, padding: ".6rem .7rem", fontSize: ".95rem", fontFamily: "inherit", resize: "vertical" }} />
            )}
            {falta === p.id && <div style={{ color: "#ef4444", fontSize: ".8rem", marginTop: 6 }}>{t("Esta pregunta es obligatoria.", "This question is required.")}</div>}
          </div>
        );
      })}

      {errorEnvio && <div style={{ ...card, borderColor: "#ef4444", color: "#b91c1c" }}>{errorEnvio}</div>}
      <button type="button" onClick={enviar} disabled={enviando}
        style={{ width: "100%", background: BRAND, color: "#fff", border: "none", borderRadius: 12, padding: ".95rem", fontSize: "1.05rem", fontWeight: 800, cursor: enviando ? "wait" : "pointer" }}>
        {enviando ? t("Enviando…", "Sending…") : t("Enviar respuestas", "Submit answers")}
      </button>
      <p style={{ textAlign: "center", color: "#8aa1b8", fontSize: ".72rem", marginTop: "1rem" }}>
        {t("No incluyas datos médicos ni números de Seguro Social en tus respuestas.", "Please don't include medical details or Social Security numbers in your answers.")}
      </p>
    </>,
  );
}
